import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { GlobalSearch, MobileSearchModal } from '@/components/search'
import { User, LogOut, Search, Bell, Check } from 'lucide-react'
import { useAuth } from '@/hooks/use-auth'
import ThemeToggle from '@/components/theme/ThemeToggle'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useUIStore } from '@/store/app-store'
import { formatDistanceToNow } from 'date-fns'
import { SidebarTrigger } from '@/components/ui/sidebar'

export default function Header() {
  const { user, signOut } = useAuth()
  const [showMobileSearch, setShowMobileSearch] = useState(false)
  const { notifications, removeNotification, markNotificationRead, markAllNotificationsRead } = useUIStore()

  const unreadCount = notifications.filter((n) => !n.read).length
  const latest = notifications.slice(0, 5)

  return (
    <header className="bg-secondary/80 border-b border-primary/10 backdrop-blur-sm supports-[backdrop-filter]:bg-secondary/60 px-6 py-3">
      <div className="flex items-center justify-between">
        {/* Left section */}
        <div className="flex items-center gap-4">
          {/* Mobile sidebar trigger - uses shadcn SidebarTrigger */}
          <SidebarTrigger className="md:hidden" />

          {/* Breadcrumb */}
          <nav className="hidden md:flex items-center space-x-2 text-sm text-muted-foreground">
            <span>Dashboard</span>
            <span>/</span>
            <span className="text-foreground">Overview</span>
          </nav>
        </div>

        {/* Center section - Global Search */}
        <div className="hidden md:flex items-center flex-1 max-w-md mx-8">
          <div className="w-full rounded-lg hover:ring-1 hover:ring-primary/20 transition-shadow">
            <GlobalSearch
              className="w-full"
              placeholder="Search galleries, services, leads..."
            />
          </div>
        </div>

        {/* Right section */}
        <div className="flex items-center gap-2">
          {/* Theme Toggle */}
          <ThemeToggle />

          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Notifications" className="relative">
                <Bell className="w-4 h-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel className="flex items-center justify-between">
                <span className="font-semibold">Notifications</span>
                {notifications.length > 0 && (
                  <button
                    className="text-xs text-primary hover:underline"
                    onClick={() => markAllNotificationsRead()}
                  >
                    Mark all read
                  </button>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {notifications.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  You're all caught up.
                </div>
              ) : (
                <ScrollArea className="max-h-80">
                  <div className="space-y-1 px-1">
                    {latest.map((notification) => (
                      <DropdownMenuItem
                        key={notification.id}
                        className={`flex items-start gap-3 py-3 ${notification.read ? 'opacity-70' : ''}`}
                        onSelect={(e) => {
                          e.preventDefault()
                          markNotificationRead(notification.id, true)
                        }}
                      >
                        <div className={`mt-1 h-2 w-2 rounded-full ${notification.read ? 'bg-muted' : 'bg-primary'}`} />
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium leading-tight">{notification.title}</p>
                            {notification.read && <Check className="h-3 w-3 text-muted-foreground" />}
                          </div>
                          {notification.message && (
                            <p className="text-xs text-muted-foreground">{notification.message}</p>
                          )}
                          <p className="text-[11px] text-muted-foreground">
                            {formatDistanceToNow(notification.timestamp, { addSuffix: true })}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground"
                          aria-label="Dismiss notification"
                          onClick={(e) => {
                            e.stopPropagation()
                            removeNotification(notification.id)
                          }}
                        >
                          ×
                        </Button>
                      </DropdownMenuItem>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Mobile Search Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden hover:scale-110 active:scale-95 transition-transform motion-reduce:transition-none"
            aria-label="Search"
            onClick={() => setShowMobileSearch(true)}
          >
            <Search className="w-4 h-4" aria-hidden="true" />
          </Button>

          {/* User Menu */}
          <div className="flex items-center gap-2 pl-2">
            <Button variant="ghost" className="h-8 px-2 gap-2 group">
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center group-hover:scale-110 transition-transform motion-reduce:transition-none">
                <User className="w-3 h-3 text-primary-foreground" aria-hidden="true" />
              </div>
              <span className="hidden md:block text-sm">{user?.name || user?.email || 'User'}</span>
            </Button>

            {/* Logout Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => signOut()}
              title="Sign out"
              aria-label="Sign out"
              className="hover:scale-110 active:scale-95 transition-transform motion-reduce:transition-none"
            >
              <LogOut className="w-4 h-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Search Modal */}
      <MobileSearchModal
        isOpen={showMobileSearch}
        onClose={() => setShowMobileSearch(false)}
      />
    </header>
  )
}
